import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { SlackService, SlackConnection, SlackEmojiOption } from '@app/core/services/slack.service'
import { RedirectService } from '@app/shared/services/redirect.service'
import { LockSseService } from '@app/core/services/lock-sse.service'

@Component({
  selector: 'app-sidebar-slack',
  templateUrl: './sidebar-slack.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SidebarSlackComponent implements OnInit {
  private slack = inject(SlackService)
  private router = inject(Router)
  private redirect = inject(RedirectService)
  private lockSse = inject(LockSseService)

  loading = signal(false)
  busy = signal(false)
  errorMsg = signal<string | null>(null)
  connection = signal<SlackConnection | null>(null)
  palette = signal<SlackEmojiOption[]>([])

  formOpen = signal(false)
  scheduleOpen = signal(false)
  accountMenuOpen = signal(false)

  form: { text: string; emoji: string; endAt: string } = { text: '', emoji: '', endAt: '' }
  scheduleForm: { text: string; emoji: string; startAt: string; endAt: string } = {
    text: '', emoji: '', startAt: '', endAt: ''
  }

  connected = computed(() => !!this.connection()?.connected)
  enabled = computed(() => this.connection()?.enabled !== false)
  hasStatus = computed(() => !!(this.connection()?.currentText || this.connection()?.currentEmoji))
  currentText = computed(() => this.connection()?.currentText)
  currentEmoji = computed(() => this.connection()?.currentEmoji)
  currentEndAt = computed(() => this.connection()?.currentEndAt)
  hasScheduled = computed(() => !!(this.connection()?.scheduledStartAt))
  scheduledText = computed(() => this.connection()?.scheduledText)
  scheduledEmoji = computed(() => this.connection()?.scheduledEmoji)
  scheduledStartAt = computed(() => this.connection()?.scheduledStartAt)
  scheduledEndAt = computed(() => this.connection()?.scheduledEndAt)
  account = computed(() => this.connection()?.account)
  lastSyncedAt = computed(() => this.connection()?.lastSyncedAt)

  emojiChar(code?: string | null): string {
    if (!code) return ''
    const hit = this.palette().find((e) => e.code === code)
    return hit ? hit.char : code
  }

  ngOnInit() {
    this.refresh()
  }

  refresh() {
    this.loading.set(true)
    this.errorMsg.set(null)
    this.slack.getConnection().subscribe({
      next: (res) => {
        const data = res?.data
        this.connection.set(data || { connected: false })
        this.palette.set(data?.emojiPalette || [])
        this.loading.set(false)
      },
      error: () => this.loading.set(false)
    })
  }

  openForm() {
    this.form = {
      text: this.currentText() || '',
      emoji: this.currentEmoji() || '',
      endAt: this.currentEndAt() || ''
    }
    this.formOpen.set(true)
    this.scheduleOpen.set(false)
  }

  closeForm() {
    this.formOpen.set(false)
    this.form = { text: '', emoji: '', endAt: '' }
  }

  openSchedule() {
    this.scheduleForm = {
      text: this.scheduledText() || '',
      emoji: this.scheduledEmoji() || '',
      startAt: this.scheduledStartAt() || '',
      endAt: this.scheduledEndAt() || ''
    }
    this.scheduleOpen.set(true)
    this.formOpen.set(false)
  }

  closeSchedule() {
    this.scheduleOpen.set(false)
    this.scheduleForm = { text: '', emoji: '', startAt: '', endAt: '' }
  }

  selectEmoji(target: 'form' | 'schedule', code: string) {
    if (target === 'form') this.form = { ...this.form, emoji: this.form.emoji === code ? '' : code }
    else this.scheduleForm = { ...this.scheduleForm, emoji: this.scheduleForm.emoji === code ? '' : code }
  }

  apply() {
    const { text, emoji, endAt } = this.form
    if (!text && !emoji) { this.errorMsg.set('Add a description or pick an emoji'); return }
    this.busy.set(true)
    this.errorMsg.set(null)
    this.slack.setStatus({ text, emoji, endAt: endAt || undefined }).subscribe({
      next: (res) => {
        this.busy.set(false)
        const d = res?.data || res
        if (d?.skipped) this.errorMsg.set(`Skipped: ${d.reason}`)
        this.closeForm()
        this.refresh()
      },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  saveSchedule() {
    const { text, emoji, startAt, endAt } = this.scheduleForm
    if (!startAt) { this.errorMsg.set('Pick a start date'); return }
    if (!text && !emoji) { this.errorMsg.set('Add a description or pick an emoji'); return }
    if (endAt && new Date(startAt) >= new Date(endAt)) { this.errorMsg.set('Turn off must be after turn on'); return }
    this.busy.set(true)
    this.errorMsg.set(null)
    this.slack.setStatus({ text, emoji, startAt, endAt: endAt || undefined }).subscribe({
      next: (res) => {
        this.busy.set(false)
        const d = res?.data || res
        if (d?.skipped) this.errorMsg.set(`Skipped: ${d.reason}`)
        this.closeSchedule()
        this.refresh()
      },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  clearStatus() {
    if (this.busy()) return
    this.busy.set(true)
    this.slack.clearStatus().subscribe({
      next: () => { this.busy.set(false); this.refresh() },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  cancelScheduled() {
    this.busy.set(true)
    this.slack.clearScheduled().subscribe({
      next: () => { this.busy.set(false); this.refresh() },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  toggleEnabled() {
    const next = !this.enabled()
    this.busy.set(true)
    this.slack.setEnabled(next).subscribe({
      next: () => { this.busy.set(false); this.refresh() },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  toggleAccountMenu() {
    this.accountMenuOpen.update((v) => !v)
  }

  closeAccountMenu() {
    this.accountMenuOpen.set(false)
  }

  onFormEndAt(value: any) { this.form = { ...this.form, endAt: typeof value === 'string' ? value : '' } }
  onScheduleStartAt(value: any) { this.scheduleForm = { ...this.scheduleForm, startAt: typeof value === 'string' ? value : '' } }
  onScheduleEndAt(value: any) { this.scheduleForm = { ...this.scheduleForm, endAt: typeof value === 'string' ? value : '' } }

  onFormText(value: string) { this.form = { ...this.form, text: value } }
  onScheduleText(value: string) { this.scheduleForm = { ...this.scheduleForm, text: value } }

  connectNow() {
    this.busy.set(true)
    this.errorMsg.set(null)
    this.slack.getAuthUrl().subscribe({
      next: (res) => {
        this.busy.set(false)
        const url = res?.data?.url
        if (url) this.lockSse.releaseLocksAndRedirect(url)
        else this.errorMsg.set('No auth URL returned')
      },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }

  disconnect() {
    if (!confirm('Disconnect Slack? Your status and schedule will be cleared.')) return
    this.busy.set(true)
    this.errorMsg.set(null)
    this.slack.disconnect().subscribe({
      next: () => { this.busy.set(false); this.refresh() },
      error: (err) => {
        this.busy.set(false)
        this.errorMsg.set(typeof err === 'string' ? err : err?.message || 'Failed')
      }
    })
  }
}
